import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@latitude/accordion';
import { useActionListContext } from 'ActionListContext';
import React, { useCallback, useMemo } from 'react';
import type { ActionListItem, CommandGroupHeaderItemProps } from 'types';

import { isValueSelected } from '../utils';
import { CommandGroup } from './CommandGroup';
import { CommandGroupItem } from './CommandGroupItem';

export const CommandGroupHeaderItem: React.FC<CommandGroupHeaderItemProps> = ({
  showSeparator,
  headerItem,
  itemSize,
  showSelectionElement,
  isTruncated = false,
}) => {
  const { selectedValues } = useActionListContext();

  const renderItems = useCallback(
    (items: ActionListItem[]) =>
      items.map((item, index) => {
        const showSubSeparator =
          item.hasSeparator && index !== items.length - 1;
        const isSelected = isValueSelected(selectedValues, item.value);

        return (
          <CommandGroupItem
            key={`${item.value}-${index}`}
            item={item}
            itemSize={itemSize}
            showSelectionElement={showSelectionElement}
            showSeparator={showSubSeparator ?? false}
            isSelected={isSelected}
            isTruncated={isTruncated}
          />
        );
      }),
    [itemSize, showSelectionElement, selectedValues, isTruncated],
  );

  const content = useMemo(() => {
    if (headerItem.isExpandable) {
      const totalSelectedItems = headerItem.items.reduce((acc, item) => {
        return isValueSelected(selectedValues, item.value) ? acc + 1 : acc;
      }, 0);

      return (
        <Accordion type="multiple" size="small">
          <AccordionItem value={headerItem.title}>
            <AccordionTrigger
              title={headerItem.title}
              badgeProps={
                totalSelectedItems
                  ? {
                      variant: 'neutral',
                      text: totalSelectedItems.toString(),
                    }
                  : undefined
              }
            />
            <AccordionContent noSpacing>
              {renderItems(headerItem.items)}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      );
    }

    return <>{renderItems(headerItem.items)}</>;
  }, [
    headerItem.items,
    headerItem.title,
    headerItem.isExpandable,
    renderItems,
    selectedValues,
  ]);

  return (
    <>
      <CommandGroup
        heading={!headerItem.isExpandable ? headerItem.title : undefined}
      >
        {content}
      </CommandGroup>
      {showSeparator ? (
        <div className="border-color-comp-table-divider my-2 border-b border-solid" />
      ) : null}
    </>
  );
};
